import { computed, ref } from 'vue';

/**
 * Row-level saving / error / rollback (P2-FE-006).
 * Tracks per-id busy state, last error, and restores a snapshot on failure.
 */
export function useQaRowAction() {
  const savingIds = ref([]);
  const rowErrors = ref({});

  const isSaving = id => savingIds.value.includes(String(id));
  const savingCount = computed(() => savingIds.value.length);
  const anySaving = computed(() => savingIds.value.length > 0);

  const markSaving = id => {
    const key = String(id);
    if (!savingIds.value.includes(key)) {
      savingIds.value = [...savingIds.value, key];
    }
  };

  const clearSaving = id => {
    const key = String(id);
    savingIds.value = savingIds.value.filter(item => item !== key);
  };

  const clearError = id => {
    const key = String(id);
    if (!(key in rowErrors.value)) return;
    const next = { ...rowErrors.value };
    delete next[key];
    rowErrors.value = next;
  };

  const setError = (id, err) => {
    rowErrors.value = {
      ...rowErrors.value,
      [String(id)]: err?.message || String(err || 'Request failed'),
    };
  };

  /**
   * @param {string|number} id
   * @param {{ applyOptimistic?: Function, execute: Function, rollback?: Function }} hooks
   */
  const runRow = async (id, { applyOptimistic, execute, rollback } = {}) => {
    if (!id) return null;
    const key = String(id);
    markSaving(key);
    clearError(key);
    try {
      if (typeof applyOptimistic === 'function') applyOptimistic();
      return await execute();
    } catch (err) {
      if (typeof rollback === 'function') rollback();
      setError(key, err);
      throw err;
    } finally {
      clearSaving(key);
    }
  };

  return {
    savingIds,
    rowErrors,
    savingCount,
    anySaving,
    isSaving,
    runRow,
    clearError,
  };
}
